import React from 'react';
import './App.scss';
import MsalProvider from "./plugins/auth/src/components/MsalProvider";
import { ThemeProvider, DataProvider, getParam, prepareInlineAdapter, PluginProvider, AuthProvider, useDataProvider, 
    PortalConfig, PortalUser, getBaseUrl, getCurrentPage, callApi, translateNamespace, User } from '@cloudpremise/portal_core';
import { useMaterialUIController } from "@cloudpremise/portal_core/src/themes/mui/context";
import { I18nextProvider, useTranslation } from 'react-i18next';
import { getI18nextInstance } from './i18n/i18n';
import { BrowserRouter, MemoryRouter } from "react-router-dom";
import { createBrowserHistory } from "history";
import CustomRouter from "./components/CustomRouter";
import PluginManager from "./plugins";
import Config from "./Config";
import CircularProgress from '@mui/material/CircularProgress';
import ChildThemeLoader from "./themes/Loader";
const cdnUrl = Config.getCdnUrl();
prepareInlineAdapter();

declare const window: Window & typeof globalThis & {
    inlineApexAdaptor: any,
    jsforce: any,
}
const history = createBrowserHistory();
const bgImage = cdnUrl + "/assets/img/lead2.jpg";

function App() {
    const dataProvider = process.env.REACT_APP_DATA_PROVIDER;
    const [controller, dispatch] = useMaterialUIController();
    const { config, loadingConfig, loadingUser } = controller;
    const i18n = getI18nextInstance();
    document.documentElement.lang = i18n.language;
    const { t } = useTranslation();
    const msalConfig = Config.getMsalConfig();
    const [state, setState] = React.useState({
        loading: true
    });
    const page = getCurrentPage();
    const basename = getBaseUrl();
    if(window.location.pathname !== page){
        history.push(page+window.location.search+window.location.hash);
    }

    React.useEffect(() => {
        loadData();
    }, []);

    function loadData(){
        const portalConfigRecord: PortalConfig = {
            sObjectTypeName: 'Config',
            url: "/v1/portal/config",
        }
        callApi(dataProvider, portalConfigRecord).then((response: any) => {
            if(response && Array.isArray(response?.result)){
                if(response.result.length > 0){
                    const config = translateNamespace(response.result[0]);
                    PluginManager.setConfiguredPlugins(config.plugins);
                    dispatch({value: {config: config, loadingConfig: false}, type: "UPDATE_STATE"});
                }else{
                    dispatch({value: {loadingConfig: false}, type: "UPDATE_STATE"});
                }
            }else{
                dispatch({value: {loadingConfig: false}, type: "UPDATE_STATE"});
            }
        }).catch((err) => {
            console.log(err);
        });

        if(loadingUser){
            const portalUserRecord: PortalUser = {
                sObjectTypeName: 'PortalUser',
                url: "/v1/identity/me",
                method: "POST"
            }
    
            callApi(dataProvider, portalUserRecord).then((response: any) => {
                if(response && Array.isArray(response?.result)){
                    if(response.result.length > 0){
                        const userObj = new User(response.result[0]);
                        dispatch({value: {user: userObj, loadingUser: false}, type: "UPDATE_STATE"});
                    }else{
                        history.push(basename+"/auth/logout"+window.location.search+window.location.hash);
                        dispatch({value: {loadingUser: false}, type: "UPDATE_STATE"});
                    }
                }else{
                    history.push(basename+"/auth/logout"+window.location.search+window.location.hash);
                    dispatch({value: {loadingUser: false}, type: "UPDATE_STATE"});
                }
            }).catch((err) => {
                console.log(err);
                history.push(basename+"/auth/logout"+window.location.search+window.location.hash);
                dispatch({value: {loadingUser: false}, type: "UPDATE_STATE"});
            });
        }
    }
    function getDefaultTheme(){
        let theme = localStorage.getItem("frontend_theme");
        if( theme === null){
            theme = process.env.REACT_APP_THEME;
        }
        return theme;
    }
    // Will need to be implemented in future. Currently it is throwing recursive state update error. Maximum stack exceeded.
    // const StackProviders = ({ children }: { children: React.ReactNode }) => {
    //     const providers = PluginManager.getProviders();
    //     if(providers.length <= 0){
    //         return children;
    //     }
    //     console.log("providers", providers);
    //     const stackedProviders = providers.reduceRight((acc, data) => {
    //         console.log("reduceRight");
    //         const { Provider, props } = data;
    //         return <Provider {...props}>{acc}</Provider>;
    //     }, children);
    //     console.log("stackedProviders", stackedProviders);
    //     return stackedProviders;
    // }
    const ReactRouter = process.env.NODE_ENV === "development" ? MemoryRouter : BrowserRouter;
    let routerProps = {
        basename: basename
    };
    if(process.env.NODE_ENV === "development"){
        routerProps['initialEntries'] = [page];
    }
    return (
        <ThemeProvider theme={getDefaultTheme()}>
            <DataProvider type={dataProvider}>
                <I18nextProvider i18n={i18n}>
                    {
                        config === null || loadingConfig || loadingUser ?
                        <div className="splash-screen-container" style={{backgroundImage: "url("+bgImage+")",backgroundSize: "cover"}}>
                                <div className="splash-screen">
                                    <CircularProgress color="info" size="60px" />
                                </div>
                            </div>
                        :
                        <MsalProvider clientId={msalConfig.auth.clientId}>
                            <PluginProvider pluginManager={PluginManager}>
                                <ReactRouter {...routerProps}>
                                    <AuthProvider authTypes={["link", "microsoft","salesforce", "google", "github", "facebook", "apple"]}>
                                        <div className="slds-scope slds-is-relative App">
                                            <div className="main-container" id="global_wrapper">
                                                <CustomRouter page={page} basename={basename} history={history} />
                                            </div>
                                        </div>
                                    </AuthProvider>
                                    {PluginManager.render()}
                                </ReactRouter>
                            </PluginProvider>
                        </MsalProvider>
                    }
                    <ChildThemeLoader />
                </I18nextProvider>
            </DataProvider>
        </ThemeProvider>
    );
}

export default App;
